// islands/KitDiscountBadge.tsx
import { kitPdpDabelleUI } from '$store/sdk/kitPdpDabelleUI.ts'
import type { TagColors as TagColorsType } from '$store/loaders/TagColor/types.ts'
import * as preact from 'preact'

interface Props {
  // pode vir `false` do loader
  tagColors?: TagColorsType | false
}

/** Blink usando wrapper interno:
 * - O <span> externo mantém SUAS classes (absolute, py, px, rounded etc.)
 * - O <span> interno herda padding/borda e desenha o fundo com ::after
 * - Resultado: só o bloco do texto pisca, sem esticar pela largura do container
 */
function BlinkBadge({
  children,
  bg = '#da1884',
  fg = '#ffffff',
  blink = true,
  speedMs = 600,
  dimOpacity = 0, // 0 = apaga total; 0.3 ~ apaga 70%
  className = '',
  style = {},
}: {
  children: preact.ComponentChildren
  bg?: string
  fg?: string
  blink?: boolean
  speedMs?: number
  dimOpacity?: number
  className?: string
  style?: preact.JSX.CSSProperties
}) {
  // classe de estado de blink
  const blinkClass = blink ? 'kdb-blink-on' : 'kdb-blink-off'

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @media (prefers-reduced-motion: reduce) {
            ._kdb_wrap_.kdb-blink-on ._kdb_inner_::after {
              animation: none !important;
            }
          }

          @keyframes kdb-blink {
            0%, 49% { opacity: 1; }
            50%, 100% { opacity: var(--dim-op, 0); }
          }

          /* span externo: mantém suas classes (absolute/rounded/py/px etc.) */
          ._kdb_wrap_{
            background: transparent !important;
            display: inline-block;
            isolation: isolate;
          }

          /* span interno: herda padding/borda do externo e desenha o fundo */
          ._kdb_inner_{
            position: relative;
            display: inline-block;
            padding: inherit;
            border-radius: inherit;
            color: var(--fg, #fff);
            background: transparent !important;
          }

          /* Blink ON: aplica animação */
          ._kdb_wrap_.kdb-blink-on ._kdb_inner_::after{
            content: "";
            position: absolute;
            inset: 0;
            border-radius: inherit;
            background: var(--bg, #da1884);
            z-index: -1;
            pointer-events: none;
            animation: kdb-blink var(--blink-speed, 600ms) steps(1,end) infinite;
          }

          /* Blink OFF: sem animação, opacidade fixa em 1 */
          ._kdb_wrap_.kdb-blink-off ._kdb_inner_::after{
            content: "";
            position: absolute;
            inset: 0;
            border-radius: inherit;
            background: var(--bg, #da1884);
            z-index: -1;
            pointer-events: none;
            animation: none !important;
            opacity: 1;
          }
        `,
        }}
      />
      <span
        className={`_kdb_wrap_ ${blinkClass} ${className}`}
        style={{
          backgroundColor: 'transparent',
          ...style,
        }}
      >
        <span
          className="_kdb_inner_"
          style={{
            ['--bg' as any]: bg,
            ['--fg' as any]: fg,
            ['--blink-speed' as any]: `${speedMs}ms`,
            ['--dim-op' as any]: `${Math.max(0, Math.min(1, dimOpacity))}`,
          }}
        >
          {children}
        </span>
      </span>
    </>
  )
}

export default function KitDiscountBadge({ tagColors }: Props) {
  const { tagKitDiscount } = kitPdpDabelleUI()
  console.log(tagColors, 'tagColors')

  // só renderiza quando houver desconto > 0
  if (!tagKitDiscount.value || tagKitDiscount.value <= 0) return null

  // garante que só usa o objeto quando NÃO for false
  const cfg = tagColors && tagColors !== false ? tagColors : undefined

  // cores
  const bg = cfg?.backgroundColorTag ?? '#DA1884'
  const fg = cfg?.textColor ?? '#FFFFFF'

  // blink config (se vier do loader/CMS)
  // se onBlink === false -> NÃO pisca
  const blinkEnabled = cfg?.onBlink === true
  const blinkSpeed = cfg?.blinkSpeedMs ?? 600
  const dimOpacity =
    typeof cfg?.blinkDimPercent === 'number'
      ? Math.max(0, Math.min(100, cfg.blinkDimPercent)) / 100
      : 0

  return (
    <BlinkBadge
      bg={bg}
      fg={fg}
      blink={blinkEnabled}
      speedMs={blinkSpeed}
      dimOpacity={dimOpacity}
      className="max-w-[90px] py-1 lg:py-[6px] text-center text-[10px] lg:text-sm leading-[17px] font-bold w-auto px-3 whitespace-nowrap"
    >
      -{tagKitDiscount.value}% OFF
    </BlinkBadge>
  )
}
